import { useParams, useNavigate } from "react-router-dom";
import ROUTES from "../../../routes/constants";
import { useFileExplorer } from "./use-file-explorer";
import { useFileExplorerFolder } from "./use-file-explorer-folder";
import { FolderSummary } from "../types";
import { toFolderSummary } from "../utils/folder-summary";

// eslint-disable-next-line import/prefer-default-export
export const useFileExplorerLogic = () => {
  const { folderName } = useParams<{ folderName: string }>();
  const navigate = useNavigate();

  const selectedFolderName = folderName ? decodeURIComponent(folderName) : null;

  const list = useFileExplorer();
  const folder = useFileExplorerFolder(selectedFolderName);

  const listSummary = selectedFolderName
    ? list.folders?.find((f) => f.name === selectedFolderName)
    : undefined;

  // Prefer the statistics from a fully loaded folder; until then use the
  // list's summary, or just the name if the list hasn't loaded yet.
  const selectedFolder: FolderSummary | null = selectedFolderName
    ? (folder.folder ??
      listSummary ??
      toFolderSummary({ name: selectedFolderName, statistics: null }))
    : null;

  // How many files the folder had when its statistics were last calculated,
  // used to show progress while its sections load.
  const expectedFileCount =
    listSummary?.fileCount != null
      ? listSummary.fileCount + (listSummary.deletedCount ?? 0)
      : null;

  const handleRefresh = () => {
    if (selectedFolderName) {
      folder.mutate();
    } else {
      list.mutate();
    }
  };

  const handleViewFolder = (summary: FolderSummary) => {
    navigate(
      `${ROUTES.ADMIN.FILE_EXPLORER}/folders/${encodeURIComponent(summary.name)}`,
    );
  };

  const handleBackToList = () => {
    navigate(ROUTES.ADMIN.FILE_EXPLORER);
  };

  return {
    selectedFolder,
    selectedFolderName,
    folders: list.folders,
    isLoading: selectedFolderName ? folder.isLoading : list.isLoading,
    error: selectedFolderName ? folder.error : list.error,
    isFolderNotFound: folder.isNotFound,
    folderBlobs: folder.blobs,
    isLoadingMoreFiles: folder.isLoadingMore,
    loadMoreFilesError: folder.loadMoreError,
    expectedFileCount,
    handleRetryLoadingFiles: folder.retry,
    handleRefresh,
    handleViewFolder,
    handleBackToList,
  };
};
